import { Box } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { InvestorDashboardLoanStatuses } from '../../../../../constants/investorDashboardStatus';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import SelectWrapper from '../../../../../shared/components/formUtilities/useSelect';
import DataTableGrid from '../../../../../shared/components/formUtilities/useTable';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { InvestorDashboardService } from '../investorDashboardService';
import { DisburseDialog } from './disburseDialog';
import { InitializeInvestorLoansColumns } from './loansColumns';

export const InvestorDashboardLoans = () => {
	const { t } = useTranslation();
	const investorDashboardService = new InvestorDashboardService();
	const [loanToDisburse, setLoanToDisburse] = useState(null);
	const columns = InitializeInvestorLoansColumns(t, setLoanToDisburse);
	const [loans, setLoans] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageCount: 0,
		perPage: 10,
		totalCount: 0,
	});

	const form = useFormik({
		initialValues: {
			loanId: '',
			businessName: '',
			status: 'all',
		},
		enableReinitialize: true,
	});

	const requestIdRef = useRef(0);

	const getLoans = (currentPage = 1, perPage = pagination.perPage) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		investorDashboardService
			.getLoans(currentPage, perPage, form.values)
			.then((resp) => {
				if (requestId !== requestIdRef.current) return;
				setLoans(resp.data?.list || []);
				setPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: resp.data.paging?.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
			})
			.finally(() => {
				if (requestId === requestIdRef.current) setLoading(false);
			});
	};

	useEffect(() => {
		getLoans();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDisbursed = () => {
		setLoanToDisburse(null);
		Toaster({ msg: t('disburseSuccess'), isError: false });
		getLoans(pagination.currentPage, pagination.perPage);
	};

	const onPageChange = (page) => getLoans(page + 1, pagination.perPage);
	const onPageSizeChange = (perPage) => getLoans(1, perPage);
	const onSearch = () => getLoans(1, pagination.perPage);

	return (
		<Box className="flex flex-col gap-4">
			<FormikProvider value={form}>
				<Box className="flex flex-wrap gap-3 items-start">
					<Box className="w-full md:w-[220px]">
						<InputWrapper label={t('loanId')} name="loanId" />
					</Box>
					<Box className="w-full md:w-[200px]">
						<InputWrapper label={t('businessName')} name="businessName" />
					</Box>
					<Box className="w-full md:w-[220px]">
						<SelectWrapper
							label={t('status')}
							name="status"
							showDefaultOption
							defaultOptionText={t('all')}
							defaultOptionValue="all"
							valueKey="id"
							labelKey="name"
							items={InvestorDashboardLoanStatuses}
						/>
					</Box>
					<Box className="flex flex-col items-start">
						<Box className="flex flex-0 items-center !relative w-full">
							<TextWrapper classList="invisible truncate-word" type="caption" text="&nbsp;" />
						</Box>
						<ButtonWrapper
							classList="!h-10"
							label={t('search')}
							onClick={onSearch}
							disabled={loading}
						/>
					</Box>
				</Box>
			</FormikProvider>
			<DataTableGrid
				columnPinningLeft={isMobile ? ['loanId'] : []}
				name="investorDashboardLoansTbl"
				loading={loading}
				onPageChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
				pagination={pagination}
				columns={columns}
				records={loans}
				isPagination
				showEditColumnsButton={false}
				headerCellClass="!px-3"
				bodyCellClass="!px-3 !py-2"
			/>
			<DisburseDialog
				state={!!loanToDisburse}
				loan={loanToDisburse}
				onClose={() => setLoanToDisburse(null)}
				onDisbursed={onDisbursed}
			/>
		</Box>
	);
};
